import { Box, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { colors } from '../../components';
import CustomButton from '../../components/button/ButtonComponent';
import DocumentPreviewModal from '../../components/DocumentPreviewModal/DocumentPreviewModal';
import { EmptyStateWithLoader } from '../../components/empty-state';
import CommonHeader from '../../components/ui-components/CommonHeader';
import { getDocumentPreview } from '../../services/documents-service';
import { RootState } from '../../store';
import { openToast } from '../../store/reducers/toastSlice';
import { trackDocumentActionFailed, trackDocumentDownload, trackDocumentPreview } from '../../utils/google-analytics';
import { downloadBlobSafely, formatToUSDate, getMimeType, isDocumentPreviewEligible } from '../../utils/helper';
import { useTranslation } from '../../utils/translations';

interface Document {
    id: number;
    name: string;
    date: string;
    type: string;
    size: string;
    fileId: string;
}

export interface ContractDocumentApiItem {
    Id: string | number;
    ReferenceType?: string;
    DocName: string;
    DocCode?: string;
    FileId: string;
    UserId?: string | null;
    ContractId?: string;
    IsCustPortalDocument?: boolean;
    Size: string;
    Type: string;
    DocumentCreatedTime?: string;
    CreatedTime?: string;
    UpdatedTime?: string;
}

interface DocumentsTabProps {
    contractId?: string;
    data?: ContractDocumentApiItem[];
    loading?: boolean;
    currentPage?: number;
    rowsPerPage?: number;
    searchTerm?: string;
    onSearchChange?: (value: string) => void;
    onTotalCountChange?: (count: number) => void;
}

const DocumentsTab: React.FC<DocumentsTabProps> = ({
    contractId,
    data = [],
    loading = false,
    currentPage = 0,
    rowsPerPage = 5,
    searchTerm = '',
    onTotalCountChange,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
    const impersonation = useSelector((state: RootState) => state.impersonation);
    const [downloadingId, setDownloadingId] = useState<string | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [previewOpen, setPreviewOpen] = useState<boolean>(false);
    const [previewingId, setPreviewingId] = useState<string | null>(null);
    const [previewData, setPreviewData] = useState<string | undefined>(undefined);
    const [fileType, setFileType] = useState<string>('');
    const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
    const [previewUnavailableIds, setPreviewUnavailableIds] = useState<Set<string>>(() => new Set());
    // Transform data when it changes
    const documents = useMemo(() => {
        if (!data || data.length === 0) return [];
        return data.map((doc: ContractDocumentApiItem) => ({
            id: Number(doc.Id),
            name: doc.DocName || 'Untitled Document',
            date: formatToUSDate(doc.DocumentCreatedTime || doc.CreatedTime || ''),
            type: doc.Type || 'PDF',
            size: doc.Size || 'N/A',
            fileId: doc.FileId,
        }));
    }, [data]);

    const searchLower = (searchTerm || '').trim().toLowerCase();
    const filteredDocuments = useMemo(() => {
        if (!searchLower) return documents;
        return documents.filter((doc: Document) => {
            const name = String(doc.name || '').toLowerCase();
            const date = String(doc.date || '').toLowerCase();
            const type = String(doc.type || '').toLowerCase();
            const size = String(doc.size || '').toLowerCase();
            return [name, date, type, size].some(part => part.includes(searchLower));
        });
    }, [documents, searchLower]);

    useEffect(() => {
        onTotalCountChange?.(filteredDocuments.length);
    }, [filteredDocuments.length, onTotalCountChange]);

    useEffect(() => {
        setPreviewUnavailableIds(new Set());
    }, [contractId]);

    const markPreviewUnavailable = useCallback((fileId: string) => {
        setPreviewUnavailableIds(prev => {
            const next = new Set(prev);
            next.add(String(fileId));
            return next;
        });
    }, []);

    const canShowPreviewForDocument = (document: Document): boolean => {
        if (impersonation?.isImpersonating) return false;
        if (!document.fileId || !document.name) return false;
        if (!isDocumentPreviewEligible(document.name)) return false;
        return !previewUnavailableIds.has(String(document.fileId));
    };

    const handlePreview = async (document: Document) => {
        // TODO: Keep contract-level document preview disabled during impersonation.
        if (impersonation?.isImpersonating) return;
        if (!canShowPreviewForDocument(document)) {
            console.warn('⚠️ Preview: Document not eligible for preview -', document.name);
            return;
        }

        try {
            setPreviewingId(document.fileId);
            console.log('👁️ Preview: Opening preview for document:', document.name, 'ID:', document.fileId);

            const response = await getDocumentPreview(document.fileId.toString(), true);
            console.log('👁️ Preview: API response status:', response.statusCode);

            if (response.statusCode === 200) {
                const base64Data = response.data;
                console.log('👁️ Preview: Base64 data received, length:', base64Data?.length || 0);

                const invalidPayload = base64Data == null || typeof base64Data !== 'string' || base64Data.trim() === '';
                if (invalidPayload) {
                    console.error('❌ Preview: Invalid payload received from API');
                    trackDocumentActionFailed('preview', 'Invalid or empty document data', contractId);
                    markPreviewUnavailable(String(document.fileId));
                    dispatch(
                        openToast({
                            show: true,
                            isAlertOpen: true,
                            message: 'Document data is invalid or empty',
                            severity: 'error',
                        }),
                    );
                    return;
                }
                const mimeType = getMimeType(document.name);
                console.log('👁️ Preview: MIME type for', document.name, ':', mimeType);
                setFileType(mimeType);

                const dataUrl = base64Data.startsWith('data:') ? base64Data : `data:${mimeType};base64,${base64Data}`;
                console.log('👁️ Preview: Data URL prepared, length:', dataUrl.length);

                setPreviewData(dataUrl);
                setSelectedDocument(document);
                setPreviewOpen(true);
                trackDocumentPreview(document.name, contractId);
                console.log('✓ Preview: Modal opened successfully');
            } else {
                console.error('❌ Preview: API returned error status:', response.statusCode);
                trackDocumentActionFailed('preview', response.message, contractId);
                markPreviewUnavailable(String(document.fileId));
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: response.message || 'Failed to load document preview',
                        severity: 'error',
                    }),
                );
            }
        } catch (err) {
            console.error('❌ Preview: Exception occurred:', err);
            console.error('❌ Preview: Error details:', {
                errorType: err instanceof Error ? err.constructor.name : typeof err,
                message: err instanceof Error ? err.message : String(err),
            });
            trackDocumentActionFailed('preview', err instanceof Error ? err.message : 'exception', contractId);
            markPreviewUnavailable(String(document.fileId));
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: 'Error loading document preview',
                    severity: 'error',
                }),
            );
        } finally {
            setPreviewingId(null);
        }
    };

    const handleClosePreview = () => {
        setPreviewOpen(false);
        setSelectedDocument(null);
        setPreviewData(undefined);
        setFileType('');
    };

    // Get paginated documents (from filtered list)
    const paginatedDocuments = useMemo(() => {
        const startIndex = currentPage * rowsPerPage;
        const endIndex = rowsPerPage === -1 ? filteredDocuments.length : startIndex + rowsPerPage;
        return filteredDocuments.slice(startIndex, endIndex);
    }, [filteredDocuments, currentPage, rowsPerPage]);

    const error = null;

    const handleDownload = async (document: Document) => {
        // TODO: Keep contract-level document download disabled during impersonation.
        if (impersonation?.isImpersonating) return;
        if (!document.fileId) {
            console.error('❌ Download: Document ID is missing');
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: 'Document ID is Missing',
                    severity: 'error',
                }),
            );
            return;
        }

        try {
            setDownloadingId(document.fileId);
            console.log('📥 Download: Starting download for document:', document.name, 'ID:', document.fileId);

            const response = await getDocumentPreview(document.fileId.toString(), true);
            console.log('📥 Download: API response status:', response.statusCode);

            if (response.statusCode === 200 && response.data) {
                const base64Data = response.data;
                console.log('📥 Download: Base64 data length:', base64Data.length);

                if (!base64Data || base64Data.length === 0) {
                    throw new Error('Received empty document data from API');
                }

                // Remove data URL prefix if present
                const base64String = base64Data.startsWith('data:')
                    ? base64Data.split(',')[1]
                    : base64Data.replace(/^data:.*,/, '');

                if (!base64String || base64String.length === 0) {
                    throw new Error('Failed to extract base64 data');
                }

                console.log('📥 Download: Base64 string extracted, length:', base64String.length);

                let byteArray: Uint8Array;
                try {
                    byteArray = Uint8Array.from(atob(base64String), c => c.charCodeAt(0));
                    console.log('📥 Download: Base64 decoded successfully, byte array length:', byteArray.length);
                } catch (decodeError) {
                    console.error('❌ Download: Failed to decode base64:', decodeError);
                    throw new Error('Invalid base64 data - decoding failed');
                }

                // Get correct MIME type from document name
                const mimeType = getMimeType(document.name);
                console.log('📥 Download: MIME type for', document.name, ':', mimeType);

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const blob = new Blob([byteArray as any], { type: mimeType });
                console.log('📥 Download: Blob created, size:', blob.size, 'bytes');

                // Create download link
                try {
                    downloadBlobSafely(blob, document.name || 'document');
                    console.log('📥 Download: Download triggered');
                } catch (downloadError) {
                    console.error('❌ Download: Failed to create/trigger download:', downloadError);
                    throw new Error('Failed to download file - browser error');
                }

                trackDocumentDownload(document.name, contractId);
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: 'Document Downloaded Successfully.',
                        severity: 'success',
                    }),
                );
            } else {
                console.error('❌ Download: API returned error status:', response.statusCode, response.message);
                trackDocumentActionFailed('download', response.message, contractId);
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: response.message || 'Failed to Download Document',
                        severity: 'error',
                    }),
                );
            }
        } catch (err) {
            const e = err as { message?: string };
            console.error('❌ Download: Exception occurred:', err);
            console.error('❌ Download: Error details:', {
                message: e?.message,
                errorType: err instanceof Error ? err.constructor.name : typeof err,
            });
            trackDocumentActionFailed('download', e?.message, contractId);
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: e?.message || 'Error Downloading Document. Please Try Again.',
                    severity: 'error',
                }),
            );
        } finally {
            setDownloadingId(null);
        }
    };

    if (error) {
        return (
            <Stack bgcolor={colors.greyPreset[90]} borderRadius={4} p={2} gap={1.5}>
                <Typography color="error" variant="body2">
                    {error}
                </Typography>
            </Stack>
        );
    }

    if (!contractId) {
        return (
            <Stack bgcolor={colors.greyPreset[90]} borderRadius={4} p={2} gap={1.5}>
                <Typography variant="h6" color="textSecondary" textAlign={'center'}>
                    No contract selected
                </Typography>
            </Stack>
        );
    }

    const isEmpty = documents.length === 0 && !loading;
    if (loading || isEmpty) {
        return <EmptyStateWithLoader loading={loading} empty={isEmpty} emptyMessage={t('common.NoRecordsAvailable')} />;
    }

    const getFileExtension = (filename: string) => {
        return filename.split('.').pop()?.toUpperCase() || '';
    };

    const convertBytesToKB = (bytes: string) => {
        const bytesNumber = parseInt(bytes);
        return `${(bytesNumber / 1024).toFixed(2)} KB`;
    };

    if (filteredDocuments.length === 0 && searchLower) {
        return (
            <Stack gap={3} mx={3}>
                <Stack borderRadius={4} p={2} gap={1.5} justifyContent="center" alignItems="center" minHeight="200px">
                    <Typography variant="h6" color="textSecondary" textAlign="center">
                        {t('common.NoRecordsAvailable')}
                    </Typography>
                </Stack>
            </Stack>
        );
    }

    return (
        <Stack
            sx={{
                justifyContent: 'space-between',
                gap: 3,
            }}
        >
            <Stack
                ref={scrollContainerRef}
                sx={{
                    bgcolor: colors.fullWhite,
                    borderRadius: 3,
                    p: isMobileOrTablet ? 1.5 : 2,
                    mx: isMobileOrTablet ? 1.5 : 3,
                    border: `1px solid ${colors.greyPreset[20]}`,
                }}
            >
                <Stack gap={1.5} sx={{ overflowY: isMobileOrTablet ? 'visible' : 'auto' }}>
                    {paginatedDocuments.map((doc: Document) => (
                        <Stack
                            direction={isMobileOrTablet ? 'column' : 'row'}
                            key={doc.id}
                            gap={isMobileOrTablet ? 1.5 : 2}
                            justifyContent={'space-between'}
                            bgcolor={colors.primaryLight[5]}
                            borderRadius={2}
                            p={1.5}
                            alignItems={isMobileOrTablet ? 'stretch' : 'center'}
                            sx={{
                                '& .mainTitle': {
                                    display: 'grid',
                                },
                            }}
                        >
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <CommonHeader
                                    mainTitle={
                                        <>
                                            <Tooltip title={doc.name} arrow placement="bottom">
                                                <Typography
                                                    variant="subtitle1"
                                                    component="span"
                                                    color="textPrimary"
                                                    noWrap
                                                >
                                                    {doc.name}
                                                </Typography>
                                            </Tooltip>
                                        </>
                                    }
                                    mainTitleFS={14}
                                    subTitleFS={14}
                                    cardGap={1.5}
                                    subTitle={
                                        <>
                                            <Typography
                                                variant="body2"
                                                component="span"
                                                color="textSecondary"
                                                sx={{ wordBreak: 'break-word' }}
                                            >
                                                {doc.date} • {getFileExtension(doc.name)} • {convertBytesToKB(doc.size)}{' '}
                                                • {doc?.type}
                                            </Typography>
                                        </>
                                    }
                                    icon="icon-documents"
                                    iconSize={20}
                                    iconBgColor={colors.primaryLight[70]}
                                    iconColor={colors.fullWhite}
                                    iconPadding={1.5}
                                />
                            </Box>
                            <Stack
                                direction="row"
                                gap={isMobileOrTablet ? 1 : 2}
                                sx={isMobileOrTablet ? { width: '100%' } : undefined}
                            >
                                {canShowPreviewForDocument(doc) && (
                                    <CustomButton
                                        startIcon={
                                            <span className="icon-eye" style={{ fontSize: 16, fontWeight: 600 }} />
                                        }
                                        variant="outlined"
                                        MDButton={true}
                                        sx={{
                                            width: isMobileOrTablet ? '100%' : 'fit-content',
                                        }}
                                        onClick={() => handlePreview(doc)}
                                        disabled={previewingId === doc.fileId || impersonation?.isImpersonating}
                                    >
                                        {t('common.Preview')}
                                    </CustomButton>
                                )}
                                <CustomButton
                                    startIcon={
                                        <span className="icon-download" style={{ fontSize: 16, fontWeight: 600 }} />
                                    }
                                    variant="contained"
                                    MDButton={true}
                                    sx={{
                                        width: isMobileOrTablet ? '100%' : 'fit-content',
                                    }}
                                    onClick={() => handleDownload(doc)}
                                    disabled={downloadingId === doc.fileId || impersonation?.isImpersonating}
                                >
                                    {t('common.Download')}
                                </CustomButton>
                            </Stack>
                        </Stack>
                    ))}
                    {loading && (
                        <Box display="flex" justifyContent="center" p={2}>
                            <Typography variant="body2" color="textSecondary">
                                Loading more documents...
                            </Typography>
                        </Box>
                    )}
                </Stack>
            </Stack>
            {/* Document Preview Modal */}
            <DocumentPreviewModal
                isOpen={previewOpen}
                onClose={handleClosePreview}
                documentName={selectedDocument?.name}
                documentDate={selectedDocument?.date}
                previewData={previewData}
                fileType={fileType}
                onDownload={
                    !impersonation?.isImpersonating && selectedDocument
                        ? () => handleDownload(selectedDocument)
                        : undefined
                }
            />
        </Stack>
    );
};

export default DocumentsTab;
